/**
 * The name a rolled weapon carries: prefix, base and suffix, spliced.
 *
 * Pure over the ids, which is the point. A save stores the name so a listing does not have to look
 * three things up, and `readRolledWeapon` re-derives it on load rather than trusting the stored
 * string, so a hand-edited save cannot put arbitrary text on another player's screen.
 *
 * The voice is the one in src/text/AGENTS.md: pre-war corporate copy stapled to a rusted gun.
 * "Deluxe Assault rifle of Loose Furniture" is doing exactly what a catalogue would have done.
 */

import { affix } from '@/sim/items/affixRegistry';
import type { RolledWeapon } from '@/sim/items/rolledWeaponContract';
import { weaponSpec } from '@/sim/weapons/gunRegistry';

/**
 * The budget a generated name fits in. Longer than a player name, because this one is read off an
 * item card rather than a scoreboard, and short enough that no list has to decide where to cut.
 *
 * Nothing here truncates. The test walks every affix against every gun and asserts the fit, so a
 * word too long fails a check rather than reaching a screen with its end missing.
 */
export const MAX_ITEM_NAME_CHARS = 56;

/** The name for a roll, from its ids alone. `name` itself is deliberately not an input. */
export function rolledWeaponName(roll: Omit<RolledWeapon, 'name'>): string {
  const prefix = affix(roll.prefixId).nameWord;
  const suffix = affix(roll.suffixId).nameWord;

  return `${prefix} ${weaponSpec(roll.weaponId).label} ${suffix}`;
}
